import React, { useMemo } from "react";
import { makeStyles } from "@mui/styles";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { Typography } from "@mui/material";
import IconButton from "@mui/material/IconButton";

import Selector, { type OptionKV } from "~styled/selector/Selector";

const useStyles = makeStyles((theme: any) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
  },
  label: {
    fontSize: "10px",
    fontWeight: 500,
    fontStyle: "normal",
    fontStretch: "normal",
    lineHeight: "1.3",
    letterSpacing: "normal",
    textAlign: "left",
    color: theme.colours.grey2,
    paddingBottom: 5,
  },

  labelPage: {
    display: "flex",
    minWidth: "115px",
    fontSize: "10px",
    alignItems: "flex-end",
    fontWeight: 500,
    fontStyle: "normal",
    fontStretch: "normal",
    lineHeight: "1.3",
    letterSpacing: "normal",
    textAlign: "left",
    color: theme.colours.grey2,
  },
  horizontalSpace: { paddingRight: "10px" },
  textItemContainer: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },

  iconDisabledButton: {
    color: theme.colours.grey2,
  },
  iconButton: {
    justifyContent: "center",
    alignItems: "flex-end",
    padding: 0,
    color: theme.colours.brownGrey,
  },
}));

export interface PaginationProps<T extends string | number = number> {
  rowsPerPage: T;
  onChangeRowsPerPage: (value: T) => void;
  page: number | string;
  onChangePage: (page: number) => void;
  totalRegistros: number | string;
  options: OptionKV<T>[];
}

function makeValueToLabel<T extends string | number>(
  options: OptionKV<T>[]
): Record<string, string> {
  const map: Record<string, string> = {};
  for (const opt of options) map[String(opt.value)] = opt.label;
  return map;
}

function Pagination<T extends string | number = number>({
  rowsPerPage,
  onChangeRowsPerPage,
  page,
  onChangePage,
  totalRegistros,
  options,
}: PaginationProps<T>): JSX.Element {
  const classes = useStyles();

  const intRowsPerPage =
    typeof rowsPerPage === "number" ? rowsPerPage : parseInt(rowsPerPage, 10);
  const intPage = typeof page === "number" ? page : parseInt(page, 10);
  const intTotalRegistros =
    typeof totalRegistros === "number"
      ? totalRegistros
      : parseInt(totalRegistros, 10);

  const leftDisabled = intPage === 0;
  const rightDisabled = intRowsPerPage * (intPage + 1) >= intTotalRegistros;

  const valueToLabel = useMemo(() => makeValueToLabel(options), [options]);

  const selectorDisplayed =
    valueToLabel[String(rowsPerPage)] ?? String(rowsPerPage);

  return (
    <div className={classes.root}>
      <div className={classes.textItemContainer}>
        <Typography variant="h3" className={classes.label}>
          ITEMS POR PAG.
        </Typography>
        <div className={classes.horizontalSpace}>
          <Selector<T>
            value={selectorDisplayed}
            onChange={onChangeRowsPerPage}
            options={options}
          />
        </div>
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "row",
          paddingTop: "5px",
        }}
      >
        <div>
          <Typography className={classes.labelPage}>
            {"Página " +
              (intTotalRegistros > 0 ? intPage + 1 : 0) +
              " de " +
              Math.ceil(intTotalRegistros / (intRowsPerPage || 1))}
          </Typography>
          <Typography className={classes.labelPage}>
            {"Total de registros " + intTotalRegistros}
          </Typography>
        </div>
        <IconButton
          onClick={() => onChangePage(intPage - 1)}
          className={classes.iconButton}
          disabled={leftDisabled}
          size="small"
        >
          <KeyboardArrowLeftIcon />
        </IconButton>
        <IconButton
          onClick={() => onChangePage(intPage + 1)}
          className={classes.iconButton}
          disabled={rightDisabled}
          size="small"
        >
          <KeyboardArrowRightIcon />
        </IconButton>
      </div>
    </div>
  );
}

export default Pagination;
